<template>
	<div
		class="profile-pizza"
		:class="{
			one,
			two,
			three,
			'four-plus': four || hasMore,
			total: showTotal,
			'extra-two-digits': moreCount > 9,
		}"
	>
		<ProfilePic
			v-for="(user, index) in usersToRender"
			:key="user.id"
			:class="{ slice: true, [`slice-${index + 1}`]: true }"
			:user="user"
			size="0"
		/>
		<div class="profile-more-count slice slice-4" v-if="hasMore">
			{{ showTotal ? moreCount : `+${moreCount}` }}
		</div>
	</div>
</template>

<script setup>
import { onMounted, ref, computed, watch } from 'vue'
import { getUser, getMyUserId, sortUsersByFullname } from '@/store/memberships'
import { useEventBus } from '@/helpers/bus'
import ProfilePic from './ProfilePic'

const MAX_USERS = 4
const MAX_MORE_COUNT = 99
const myId = getMyUserId()

const props = defineProps({
	size: {
		type: [String, Number],
		default: 50,
	},
	userIds: {
		type: Array,
		required: true,
	},
	includeMe: {
		type: Boolean,
		default: false,
	},
	showTotal: {
		type: Boolean,
		default: false,
	},
	alphabetical: {
		type: Boolean,
		default: false,
	},
})

const users = ref([])
const userIds = computed(() =>
	props.includeMe ? props.userIds : props.userIds.filter((id) => id !== myId)
)
const one = computed(() => userIds.value.length === 1)
const two = computed(() => userIds.value.length === 2)
const three = computed(() => userIds.value.length === 3)
const four = computed(() => userIds.value.length === 4)
const hasMore = computed(() => userIds.value.length > 4)
const moreCount = computed(() => {
	const count = props.showTotal
		? userIds.value.length
		: userIds.value.length - MAX_USERS + 1
	if (count > MAX_MORE_COUNT) return MAX_MORE_COUNT
	return count
})
const usersToRender = computed(() =>
	hasMore.value ? users.value.slice(0, MAX_USERS - 1) : users.value
)
const size = computed(() =>
	isNaN(props.size) ? props.size : props.size + 'px'
)

onMounted(() => getUsers())

useEventBus('user-update', () => getUsers())

watch(
	() => props.userIds,
	() => getUsers()
)

function getUsers() {
	const myId = getMyUserId()
	// if the DM is only between me and myself, show just one of my profile pic
	const isSelfDM = props.userIds.every((id) => id === myId)
	if (isSelfDM) {
		users.value = [getUser(myId)]
		return
	}
	// show all the other members' profile pics except my own
	// and sort them by fullname
	const userObjs = props.userIds.map((id) => getUser(id)).filter((u) => u)
	if (props.alphabetical) {
		userObjs.sort(sortUsersByFullname)
	}
	users.value = props.includeMe
		? userObjs
		: userObjs.filter((u) => u.id !== myId)
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.profile-pizza {
	--slice-width: calc(50% - 0.5px);
	width: v-bind(size);
	height: v-bind(size);
	position: relative;
	border-radius: 50%;
	overflow: hidden;
	border: 1px solid var(--neutral-200);

	&.total {
		.profile-more-count {
			padding-left: 12%; // magic number from testing various sizes
		}
	}

	&.extra-two-digits {
		.profile-more-count {
			padding-top: 6%;
			font-size: calc(
				v-bind(size) / 5
			); // magic number from testing various sizes
			padding-left: 2%; // magic number from testing various sizes
		}
	}

	&.one {
		.slice {
			width: 100%;
			height: 100%;
			:deep(.profile-pic-initials) {
				padding-right: unset !important;
				justify-content: center !important;
				font-size: calc(
					var(--size) / 2.25
				); // magic number from testing various sizes
			}
		}
	}
	&.two {
		.slice {
			width: 50%;
			height: 100%;
		}
	}
	&.three {
		.slice {
			&.slice-2 {
				top: 0;
				right: 0;
				width: 50%;
				height: 100%;
				:deep(.profile-pic-initials) {
					padding-left: 16% !important;
				}
			}
		}
	}
	&.four {
		.slice {
			left: 50%;
		}
	}

	.slice {
		--position-offset: 50%;
		--initials-padding-offset: 10%;
		position: absolute;
		width: var(--slice-width);
		height: var(--slice-width);
		background: var(--neutral-500);
		border: 0 !important;

		&.slice-1 {
			top: 0;
			left: 0;
			margin-right: 0.5px;
			margin-bottom: 0.5px;
		}
		&.slice-2 {
			top: 0;
			left: var(--position-offset);
			margin-left: 0.5px;
			margin-bottom: 0.5px;
		}
		&.slice-3 {
			top: var(--position-offset);
			left: 0;
			margin-right: 0.5px;
			margin-top: 0.5px;
		}
		&.slice-4 {
			top: var(--position-offset);
			left: var(--position-offset);
			margin-left: 0.5px;
			margin-top: 0.5px;
		}
		&.slice-1,
		&.slice-3 {
			:deep(.profile-pic-initials) {
				justify-content: flex-end;
				padding-right: var(--initials-padding-offset);
			}
		}
		&.slice-2,
		&.slice-4 {
			:deep(.profile-pic-initials) {
				justify-content: flex-start;
				padding-left: var(--initials-padding-offset);
			}
		}
		&.slice-3,
		&.slice-4 {
			:deep(.profile-pic-initials) {
				padding-bottom: var(--initials-padding-offset);
			}
		}
		:deep(.profile-pic-initials) {
			@include fontFamily(900);
			font-size: calc(
				v-bind(size) / 5
			); // magic number from testing various sizes
		}
	}
	.profile-more-count {
		position: absolute;
		height: var(--slice-width);
		width: var(--slice-width);
		padding: 2% 0 0 2%; // magic number from testing various sizes
		@include fontFamily(900);
		font-size: 10px;
		color: white;
		background: var(--neutral-500);
		font-size: calc(
			v-bind(size) / 3.5
		); // magic number from testing various sizes
	}
}
</style>
